import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import {
  useAccounts,
  AccountSelect,
  NoAccountsWarning,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Finance Setup — GL Settings (Phase B enabler)
 *
 * Maps each subledger action to the control account it should post against.
 * Fee collections, expenses, asset purchases, depreciation and disposals all
 * silently no-op until every key here is set — so this is the one-time screen
 * that "turns on" auto-posting.
 *
 * Backend: gl-settings/list, gl-settings/set.
 */

// The 9 keys the backend recognises, with the account type each should point at.
const SETTINGS = [
  { key: "FEE_CASH", label: "Fee Collection — Cash / Bank", types: ["asset"], hint: "Debited when a fee is collected" },
  { key: "FEE_INCOME", label: "Fee Income", types: ["income"], hint: "Credited when a fee is collected" },
  { key: "EXPENSE_CASH", label: "Expense — Cash / Bank", types: ["asset"], hint: "Credited when an expense is paid in cash" },
  { key: "EXPENSE_DEFAULT", label: "Default Expense Account", types: ["expense"], hint: "Debited for expenses without a specific account" },
  { key: "ASSET_DEFAULT", label: "Default Asset Account", types: ["asset"], hint: "Debited when a fixed asset is purchased" },
  { key: "DEPRECIATION_EXPENSE", label: "Depreciation Expense", types: ["expense"], hint: "Debited when depreciation is posted" },
  { key: "ACCUMULATED_DEPRECIATION", label: "Accumulated Depreciation", types: ["asset"], hint: "Credited when depreciation is posted (contra-asset)" },
  { key: "DISPOSAL_GAIN", label: "Gain on Disposal", types: ["income"], hint: "Credited when an asset is sold above net book value" },
  { key: "DISPOSAL_LOSS", label: "Loss on Disposal", types: ["expense"], hint: "Debited when an asset is sold below net book value" },
];

const FinanceSetup = () => {
  const { accounts, loading: accountsLoading } = useAccounts();

  const [values, setValues] = useState({}); // key -> account_id
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.glSettingsList,
        JSON.stringify(financeAuthPayload())
      );
      const map = {};
      if (res?.result && Array.isArray(res.data)) {
        res.data.forEach((s) => {
          map[s.setting_key] = String(s.account_id);
        });
      }
      setValues(map);
    } catch (err) {
      console.error("GL settings fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveOne = async (key) => {
    const accountId = values[key];
    if (!accountId) {
      toast.warning("Select an account first.");
      return;
    }
    setSavingKey(key);
    try {
      const res = await simplePostCall(
        ApiConfig.glSettingsSet,
        JSON.stringify({
          ...financeAuthPayload(),
          setting_key: key,
          account_id: Number(accountId),
        })
      );
      if (res?.result) {
        toast.success("Saved.");
        fetchSettings();
      } else {
        toast.error(res?.message || "Failed to save.");
      }
    } catch (err) {
      toast.error("An error occurred while saving.");
    } finally {
      setSavingKey(null);
    }
  };

  const configuredCount = SETTINGS.filter((s) => values[s.key]).length;
  const allSet = configuredCount === SETTINGS.length;
  const noAccounts = !accountsLoading && accounts.length === 0;

  return (
    <div className="finance-page-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Finance Setup — GL Settings</h4>
      </div>

      <div className="yc-list-body">
        <div className="fin-stat-row">
          <div
            className={`fin-stat-card ${
              allSet ? "fin-accent-income" : "fin-accent-liability"
            }`}
          >
            <span className="fin-stat-label">Configured</span>
            <span className="fin-stat-value">
              {configuredCount} / {SETTINGS.length}
            </span>
            <span className="fin-stat-sub">
              {allSet ? "Auto-posting active" : "Auto-posting off until all set"}
            </span>
          </div>
        </div>

        {!allSet && (
          <div className="fin-inline-warning" style={{ margin: "0 20px 12px" }}>
            Until all {SETTINGS.length} accounts are mapped, fee / expense / asset
            auto-posting and depreciation / disposal silently do nothing (not an
            error). Create the accounts in{" "}
            <strong>Chart of Accounts</strong> first, then map them here.
          </div>
        )}

        {noAccounts && (
          <div style={{ margin: "0 20px 12px" }}>
            <NoAccountsWarning />
          </div>
        )}

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : (
          <div className="yc-list-row-list">
            {SETTINGS.map((s) => {
              const isSet = !!values[s.key];
              return (
                <div key={s.key} className="yc-list-row-card">
                  <div
                    className="yc-list-grid"
                    style={{
                      gridTemplateColumns: "1.4fr 1.6fr auto",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <div className="yc-list-cell">
                      <div className="fw-semibold">
                        {s.label}{" "}
                        <span
                          className={`fin-status ${
                            isSet ? "fin-status-active" : "fin-status-reversed"
                          }`}
                          style={{ marginLeft: 6 }}
                        >
                          {isSet ? "Set" : "Not set"}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {s.hint}
                      </div>
                    </div>
                    <div className="yc-list-cell">
                      <AccountSelect
                        accounts={accounts}
                        types={s.types}
                        value={values[s.key] || ""}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, [s.key]: e.target.value }))
                        }
                        placeholder="Select account"
                      />
                    </div>
                    <div className="yc-list-cell">
                      <Button
                        size="sm"
                        className="btn btn-primary"
                        onClick={() => saveOne(s.key)}
                        disabled={savingKey === s.key}
                      >
                        {savingKey === s.key ? <Spinner size="sm" /> : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceSetup;
